// Date-aware FX conversion helpers used across financial reports.
// Mirrors the priority logic in useConsolidatedBalances.convertBalance, but
// picks the latest rate effective on or before a given date.

export interface RateRow {
  currency_pair: string; // e.g. "USD/ETB"
  rate: number;
  effective_date: string; // ISO date
  bank_account_id?: string | null;
  rate_type?: string | null; // 'bank_rate' | 'competitive_rate'
}

export type ConvertMethod = 'direct' | 'inverse' | 'etb-routed' | 'same';

export function getRateAsOf(rates: RateRow[], pair: string, date: string): number | null {
  let best: RateRow | null = null;
  for (const r of rates) {
    if (r.currency_pair !== pair) continue;
    if (r.effective_date > date) continue;
    if (!best || r.effective_date > best.effective_date) best = r;
  }
  return best ? Number(best.rate) : null;
}

/**
 * Account-aware rate lookup:
 *   1) If bankAccountId is provided, prefer the latest rate scoped to that account.
 *   2) Otherwise, if a preferred rate_type is given (e.g. the paying bank
 *      account's `exchange_rate_type`), use the latest account-agnostic row of
 *      that type.
 *   3) Otherwise fall back to an account-agnostic bank_rate row.
 *   4) If no bank_rate row exists, fall back to any account-agnostic row.
 */
export function getRateAsOfForAccount(
  rates: RateRow[],
  pair: string,
  date: string,
  bankAccountId?: string | null,
  preferredRateType?: string | null
): number | null {
  if (bankAccountId) {
    let best: RateRow | null = null;
    for (const r of rates) {
      if (r.currency_pair !== pair) continue;
      if (r.bank_account_id !== bankAccountId) continue;
      if (r.effective_date > date) continue;
      if (!best || r.effective_date > best.effective_date) best = r;
    }
    if (best) return Number(best.rate);
  }
  let bestPreferred: RateRow | null = null;
  let bestBank: RateRow | null = null;
  let bestAny: RateRow | null = null;
  for (const r of rates) {
    if (r.currency_pair !== pair) continue;
    if (r.bank_account_id) continue;
    if (r.effective_date > date) continue;
    if (preferredRateType && r.rate_type === preferredRateType) {
      if (!bestPreferred || r.effective_date > bestPreferred.effective_date) bestPreferred = r;
    }
    if (r.rate_type === 'bank_rate') {
      if (!bestBank || r.effective_date > bestBank.effective_date) bestBank = r;
    }
    if (!bestAny || r.effective_date > bestAny.effective_date) bestAny = r;
  }
  if (bestPreferred) return Number(bestPreferred.rate);
  if (bestBank) return Number(bestBank.rate);
  if (bestAny) return Number(bestAny.rate);
  return null;
}


export function convertAsOfForAccount(
  amount: number,
  from: string,
  to: string,
  date: string,
  rates: RateRow[],
  bankAccountId?: string | null,
  preferredRateType?: string | null
): { converted: number; rate: number; method: ConvertMethod } | null {
  if (!from || from === to) {
    return { converted: amount, rate: 1, method: 'same' };
  }
  const lookup = (pair: string) =>
    getRateAsOfForAccount(rates, pair, date, bankAccountId, preferredRateType);

  const direct = lookup(`${from}/${to}`);
  if (direct) return { converted: amount * direct, rate: direct, method: 'direct' };

  const inverse = lookup(`${to}/${from}`);
  if (inverse && inverse !== 0) {
    const r = 1 / inverse;
    return { converted: amount * r, rate: r, method: 'inverse' };
  }

  if (from !== 'ETB' && to !== 'ETB') {
    let fromToEtb = lookup(`${from}/ETB`);
    if (!fromToEtb) {
      const inv = lookup(`ETB/${from}`);
      if (inv && inv !== 0) fromToEtb = 1 / inv;
    }
    let etbToTarget = lookup(`ETB/${to}`);
    if (!etbToTarget) {
      const inv = lookup(`${to}/ETB`);
      if (inv && inv !== 0) etbToTarget = 1 / inv;
    }
    if (fromToEtb && etbToTarget) {
      const r = fromToEtb * etbToTarget;
      return { converted: amount * r, rate: r, method: 'etb-routed' };
    }
  }
  return null;
}


export function convertAsOf(
  amount: number,
  from: string,
  to: string,
  date: string,
  rates: RateRow[]
): { converted: number; rate: number; method: ConvertMethod } | null {
  if (!from || from === to) {
    return { converted: amount, rate: 1, method: 'same' };
  }

  // Direct
  const direct = getRateAsOf(rates, `${from}/${to}`, date);
  if (direct) return { converted: amount * direct, rate: direct, method: 'direct' };

  // Inverse
  const inverse = getRateAsOf(rates, `${to}/${from}`, date);
  if (inverse && inverse !== 0) {
    const r = 1 / inverse;
    return { converted: amount * r, rate: r, method: 'inverse' };
  }

  // Route via ETB
  if (from !== 'ETB' && to !== 'ETB') {
    let fromToEtb = getRateAsOf(rates, `${from}/ETB`, date);
    if (!fromToEtb) {
      const inv = getRateAsOf(rates, `ETB/${from}`, date);
      if (inv && inv !== 0) fromToEtb = 1 / inv;
    }
    let etbToTarget = getRateAsOf(rates, `ETB/${to}`, date);
    if (!etbToTarget) {
      const inv = getRateAsOf(rates, `${to}/ETB`, date);
      if (inv && inv !== 0) etbToTarget = 1 / inv;
    }
    if (fromToEtb && etbToTarget) {
      const r = fromToEtb * etbToTarget;
      return { converted: amount * r, rate: r, method: 'etb-routed' };
    }
  }

  return null;
}
